import { useEffect, useState } from 'react';
import type { DragEvent } from 'react';

import { DesignerInfoContextMenuItems } from '@/components/designer-info-context-menu-items';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { use_page_scroll_state } from '@/hook/use_page_scroll_state';
import { get_asset_resource_url } from '@/lib/asset-resource-url';

type ScrollToTopButtonRenderState = {
  should_show_page_indicator: boolean;
  should_show_top_button: boolean;
};

type ScrollToTopButtonProps = {
  current_asset_page_number: number;
  on_refresh_menu_select: () => void;
  total_asset_page_count: number;
};

const SCROLL_VISIBILITY_THRESHOLD = 240;
const PAGE_INDICATOR_SCROLL_SETTLE_DELAY_MS = 1600;
const PAGE_INDICATOR_SCROLL_START_DELAY_MS = 0;
const SCROLL_TO_TOP_ICON_SOURCE_URL =
  get_asset_resource_url('back-to-top-icon.png');
const SCROLL_TO_TOP_BUTTON_CLASS_NAME =
  'fixed bottom-40 right-3 z-50 touch-manipulation select-none border-0 bg-transparent p-0 cursor-pointer animate-in fade-in-0 duration-[520ms]';
const SCROLL_TO_TOP_ICON_CLASS_NAME =
  'block h-10 w-10 object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.2)]';
const PAGE_SCROLL_INDICATOR_CLASS_NAME =
  'fixed bottom-52 right-3 z-50 flex h-10 w-10 select-none flex-col items-center justify-center rounded-full bg-[#fdfdfb] text-[#161616] shadow-[0_2px_8px_rgba(0,0,0,0.16)] animate-in fade-in-0 duration-[520ms]';
const PAGE_SCROLL_INDICATOR_NUMBER_CLASS_NAME =
  'block text-[12px] font-medium leading-[13px] tracking-normal';
const PAGE_SCROLL_INDICATOR_DIVIDER_CLASS_NAME =
  'my-0.5 h-px w-5 bg-[#161616]';

export function ScrollToTopButton({
  current_asset_page_number,
  on_refresh_menu_select,
  total_asset_page_count,
}: ScrollToTopButtonProps) {
  const [is_scroll_to_top_button_visible, set_is_scroll_to_top_button_visible] =
    useState(false);
  const is_page_scrolling = use_page_scroll_state({
    scroll_settle_delay_ms: PAGE_INDICATOR_SCROLL_SETTLE_DELAY_MS,
    scroll_start_delay_ms: PAGE_INDICATOR_SCROLL_START_DELAY_MS,
  });
  const scroll_to_top_button_render_state =
    get_scroll_to_top_button_render_state({
      is_page_scrolling,
      is_scroll_to_top_button_visible,
    });

  useEffect(() => {
    const update_scroll_to_top_button_visibility = () => {
      set_is_scroll_to_top_button_visible(
        window.scrollY > SCROLL_VISIBILITY_THRESHOLD,
      );
    };

    update_scroll_to_top_button_visibility();
    window.addEventListener('scroll', update_scroll_to_top_button_visibility, {
      passive: true,
    });

    return () => {
      window.removeEventListener(
        'scroll',
        update_scroll_to_top_button_visibility,
      );
    };
  }, []);

  if (!scroll_to_top_button_render_state.should_show_top_button) {
    return null;
  }

  return (
    <>
      {scroll_to_top_button_render_state.should_show_page_indicator ?
        <PageScrollIndicator
          current_asset_page_number={current_asset_page_number}
          total_asset_page_count={total_asset_page_count}
        />
      : null}
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <button
            aria-label="Back to top"
            className={SCROLL_TO_TOP_BUTTON_CLASS_NAME}
            onClick={handle_scroll_to_top_button_click}
            onDragStart={handle_scroll_to_top_button_drag_event}
            type="button"
          >
            <img
              alt=""
              className={SCROLL_TO_TOP_ICON_CLASS_NAME}
              draggable={false}
              src={SCROLL_TO_TOP_ICON_SOURCE_URL}
            />
          </button>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <DesignerInfoContextMenuItems />
          <ContextMenuItem onSelect={on_refresh_menu_select}>
            刷新
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </>
  );
}

export function get_scroll_to_top_button_render_state({
  is_page_scrolling,
  is_scroll_to_top_button_visible,
}: {
  is_page_scrolling: boolean;
  is_scroll_to_top_button_visible: boolean;
}): ScrollToTopButtonRenderState {
  return {
    should_show_page_indicator:
      is_scroll_to_top_button_visible && is_page_scrolling,
    should_show_top_button: is_scroll_to_top_button_visible,
  };
}

function PageScrollIndicator({
  current_asset_page_number,
  total_asset_page_count,
}: {
  current_asset_page_number: number;
  total_asset_page_count: number;
}) {
  const display_asset_page_number = get_display_asset_page_number({
    current_asset_page_number,
    total_asset_page_count,
  });

  return (
    <div
      aria-label={`Page ${display_asset_page_number} of ${total_asset_page_count}`}
      className={PAGE_SCROLL_INDICATOR_CLASS_NAME}
      role="status"
    >
      <span className={PAGE_SCROLL_INDICATOR_NUMBER_CLASS_NAME}>
        {display_asset_page_number}
      </span>
      <span
        aria-hidden="true"
        className={PAGE_SCROLL_INDICATOR_DIVIDER_CLASS_NAME}
      />
      <span className={PAGE_SCROLL_INDICATOR_NUMBER_CLASS_NAME}>
        {total_asset_page_count}
      </span>
    </div>
  );
}

function get_display_asset_page_number({
  current_asset_page_number,
  total_asset_page_count,
}: {
  current_asset_page_number: number;
  total_asset_page_count: number;
}) {
  return Math.min(
    Math.max(current_asset_page_number, 1),
    total_asset_page_count,
  );
}

function handle_scroll_to_top_button_click() {
  window.scrollTo({
    behavior: 'smooth',
    top: 0,
  });
}

function handle_scroll_to_top_button_drag_event(
  drag_event: DragEvent<HTMLButtonElement>,
) {
  drag_event.preventDefault();
}
